import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ArrowLeft, Upload, Trash2, Download, File, Image, FileText, Music, Video as VideoIcon, FileSpreadsheet, FileArchive, FolderPlus, Folder, ChevronRight, MoreVertical, FolderInput, Pencil, Lock, Clapperboard, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { createUserAsset, createUserFolder, deleteUserAsset, deleteUserFolder, updateUserAsset, updateUserFolder, uploadAssetFile, getAssetSignedUrl, UserAsset, UserAssetFolder, markVideoNoResourcesNeeded, markAllVideosNoResourcesNeeded, getOrCreateVideoFolder } from '@/services/data/userAssets';
import { VideoContent } from '@/services/data/videoContent';
import { useAuthStore } from '@/stores/authStore';
import { useUserAssetsStore } from '@/stores/userAssetsStore';
import { getVirtualFiles, createVirtualFile, deleteVirtualFile, VirtualFile, sanitizeFolderName } from '@/services/data/rushesVirtualFolders';
import { uploadFileToS3, getS3DownloadUrl } from '@/services/data/s3Files';
import { toast } from 'sonner';

// Types for folder navigation
type FolderType = 'root' | 'general' | 'video' | 'archive';
type NavigationItem = {
  type: FolderType;
  id: string | null;
  name: string;
  videoContent?: VideoContent;
};

// Video status that allows editing (only draft)
const EDITABLE_VIDEO_STATUSES = ['draft'];
// Active video statuses (shown in main list)
const ACTIVE_VIDEO_STATUSES = ['draft', 'processing'];
// Archived statuses (only published goes to archive)
const ARCHIVED_VIDEO_STATUSES = ['published'];
const getVideoStatusLabel = (status: string | null) => {
  switch (status) {
    case 'draft':
      return 'En production';
    case 'processing':
      return 'Post-production';
    case 'to_validate':
      return 'À valider';
    case 'to_publish':
      return 'Programmée';
    case 'published':
      return 'Publiée';
    default:
      return status || 'Inconnu';
  }
};

// Check if a video should show a green check indicator
const hasGreenCheck = (video: VideoContent, hasAssets: boolean): boolean => {
  // processing always shows green check
  if (video.status === 'processing') {
    return true;
  }
  // draft shows green check if has assets OR resources_not_needed is true
  if (video.status === 'draft') {
    return hasAssets || video.resources_not_needed === true;
  }
  return false;
};

// Check if a video folder is editable (only draft)
const isVideoEditable = (video: VideoContent): boolean => {
  return EDITABLE_VIDEO_STATUSES.includes(video.status || '');
};
const getFileIcon = (fileType: string | null) => {
  if (!fileType) return File;
  if (fileType.startsWith('image/')) return Image;
  if (fileType.startsWith('video/')) return VideoIcon;
  if (fileType.startsWith('audio/')) return Music;
  if (fileType.includes('pdf') || fileType.includes('document')) return FileText;
  if (fileType.includes('spreadsheet') || fileType.includes('excel')) return FileSpreadsheet;
  if (fileType.includes('zip') || fileType.includes('archive')) return FileArchive;
  return File;
};
const formatFileSize = (bytes: number | null) => {
  if (!bytes) return 'Taille inconnue';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Helper to get clapperboard icon color class
const getClapperboardColorClass = (isPendingResources: boolean, isEditable: boolean): string => {
  if (isPendingResources) return 'text-pending';
  if (isEditable) return 'text-warning';
  return 'text-muted-foreground';
};

// Helper to get video title display class for editable videos
const getEditableVideoTitleClass = (): string => {
  return 'text-foreground';
};

// Helper to get video title display class for read-only videos
const getReadOnlyVideoTitleClass = (): string => {
  return 'text-muted-foreground';
};

export default function UserResourcesScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const videoIdFromUrl = searchParams.get('videoId');
  const {
    userId
  } = useAuthStore();
  const {
    assets,
    folders,
    allFolders,
    videoContents,
    videosWithPendingResources,
    generalFolderId,
    currentView,
    currentVideoContent,
    currentFolderId,
    folderPath,
    isLoading,
    loadInitialData,
    loadAllFolders,
    loadContent,
    refreshPendingResources,
    setCurrentView,
    setCurrentVideoContent,
    setCurrentFolderId,
    setFolderPath,
    navigateToFolder,
    navigateBack,
    resetNavigation,
    removeAsset,
    removeFolder,
    updateAsset,
    updateFolder,
    updateVideoContent,
    updateVideoContents
  } = useUserAssetsStore();

  // Navigation state - tracks where we are in the folder structure
  const [isUploading, setIsUploading] = useState(false);
  const [showNewFolderDialog, setShowNewFolderDialog] = useState(false);
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [showRenameDialog, setShowRenameDialog] = useState(false);
  const [showRenameFolderDialog, setShowRenameFolderDialog] = useState(false);
  const [assetToMove, setAssetToMove] = useState<UserAsset | null>(null);
  const [assetToRename, setAssetToRename] = useState<UserAsset | null>(null);
  const [folderToRename, setFolderToRename] = useState<UserAssetFolder | null>(null);
  const [newFolderName, setNewFolderName] = useState('');
  const [newAssetName, setNewAssetName] = useState('');
  const [renameFolderName, setRenameFolderName] = useState('');
  const [isCreatingFolder, setIsCreatingFolder] = useState(false);
  const [isMoving, setIsMoving] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [isRenamingFolder, setIsRenamingFolder] = useState(false);
  const [isMarkingNoResources, setIsMarkingNoResources] = useState(false);
  const [isMarkingAllNoResources, setIsMarkingAllNoResources] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // S3/Virtual files state for videos with user_assets_folder_id
  const [s3Files, setS3Files] = useState<VirtualFile[]>([]);
  const [isLoadingS3Files, setIsLoadingS3Files] = useState(false);
  const [deletingS3FileId, setDeletingS3FileId] = useState<string | null>(null);
  const [downloadingS3FileId, setDownloadingS3FileId] = useState<string | null>(null);

  // Check if current video uses S3 storage (has user_assets_folder_id)
  const useS3Storage = currentView === 'video' && currentVideoContent?.user_assets_folder_id;

  // Check if current video folder is editable
  const isCurrentVideoEditable = currentVideoContent ? EDITABLE_VIDEO_STATUSES.includes(currentVideoContent.status || '') : false;

  // Determine if we can add/modify content in current location
  const canModifyContent = currentView === 'general' || currentView === 'video' && isCurrentVideoEditable;

  // Separate videos into active (draft, processing) and archived (published only)
  const activeVideos = videoContents.filter(v => ACTIVE_VIDEO_STATUSES.includes(v.status || ''));
  const allDraftVideos = videoContents.filter(v => EDITABLE_VIDEO_STATUSES.includes(v.status || ''));
  const archivedVideos = videoContents.filter(v => ARCHIVED_VIDEO_STATUSES.includes(v.status || ''));

  // Check if all draft videos are marked as no resources needed
  const allDraftVideosNoResources = allDraftVideos.length > 0 && allDraftVideos.every(v => v.resources_not_needed === true);
  useEffect(() => {
    if (userId) {
      loadInitialData(userId);
    }
  }, [userId]);
  useEffect(() => {
    if (videoIdFromUrl && videoContents.length > 0 && !isLoading) {
      const targetVideo = videoContents.find(v => v.id === videoIdFromUrl);
      if (targetVideo) {
        handleOpenVideoFolder(targetVideo);
      }
    }
  }, [videoIdFromUrl, videoContents, isLoading]);
  useEffect(() => {
    if (userId && currentView !== 'root') {
      loadContent(userId, currentFolderId);
    }
  }, [userId, currentFolderId, currentView]);
  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !userId || !canModifyContent) return;
    setIsUploading(true);
    try {
      // Check if we should use S3 storage (video with user_assets_folder_id)
      if (useS3Storage && currentVideoContent?.user_assets_folder_id) {
        // Upload to S3
        for (const file of Array.from(files)) {
          // Build S3 key: user-assets/{userId}/{videoId}/{sanitized-filename}
          const sanitizedFileName = sanitizeFolderName(file.name.replace(/\.[^/.]+$/, '')) + 
            file.name.substring(file.name.lastIndexOf('.'));
          const s3Key = `user-assets/${userId}/${currentVideoContent.id}/${Date.now()}-${sanitizedFileName}`;
          
          const uploadResult = await uploadFileToS3(file, s3Key);
          if (!uploadResult.success) {
            toast.error(`Erreur upload: ${uploadResult.error}`);
            continue;
          }
          
          // Create virtual file entry
          const fileResult = await createVirtualFile(
            s3Key,
            file.name,
            currentVideoContent.user_assets_folder_id,
            file.size,
            new Date().toISOString()
          );
          
          if (fileResult.success && fileResult.file) {
            const newFile = fileResult.file;
            setS3Files(prev => [newFile, ...prev]);
          }
        }
        toast.success('Fichier(s) uploadé(s) avec succès');
        await refreshPendingResources(userId);
      } else {
        // Old system: upload to Supabase Storage
        for (const file of Array.from(files)) {
          const { filePath } = await uploadAssetFile(userId, file);
          await createUserAsset({
            user_id: userId,
            file_name: file.name,
            file_path: filePath,
            file_type: file.type,
            file_size: file.size,
            folder_id: currentFolderId
          });
        }
        await loadContent(userId, currentFolderId);
        await refreshPendingResources(userId);
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  const handleCreateFolder = async () => {
    if (!userId || !newFolderName.trim() || !canModifyContent) return;
    setIsCreatingFolder(true);
    try {
      await createUserFolder({
        user_id: userId,
        name: newFolderName.trim(),
        parent_folder_id: currentFolderId
      });
      setNewFolderName('');
      setShowNewFolderDialog(false);
      await loadContent(userId, currentFolderId);
      await loadAllFolders(userId);
    } catch (error) {
      console.error('Error creating folder:', error);
    } finally {
      setIsCreatingFolder(false);
    }
  };
  const handleOpenGeneralFolder = () => {
    if (!generalFolderId) return;
    setCurrentView('general');
    setCurrentFolderId(generalFolderId);
    setCurrentVideoContent(null);
    setFolderPath([]);
  };
  const handleOpenVideoFolder = async (video: VideoContent) => {
    if (!userId) return;
    setCurrentView('video');
    setCurrentVideoContent(video);
    setFolderPath([]);
    setS3Files([]); // Reset S3 files

    // Check if this video uses S3 storage
    if (video.user_assets_folder_id) {
      // Load files from S3/virtual system
      setIsLoadingS3Files(true);
      try {
        const files = await getVirtualFiles(video.user_assets_folder_id);
        setS3Files(files);
        setCurrentFolderId(video.user_assets_folder_id);
      } catch (error) {
        console.error('Error loading S3 files:', error);
      } finally {
        setIsLoadingS3Files(false);
      }
    } else {
      // Old system: use user_asset_folders
      try {
        const folder = await getOrCreateVideoFolder(userId, video.id, video.title);
        setCurrentFolderId(folder.id);
        await loadAllFolders(userId);
      } catch (error) {
        console.error('Error getting/creating video folder:', error);
        setCurrentFolderId('__video_no_folder__');
      }
    }
  };

  // S3 file handlers
  const handleDownloadS3File = async (file: VirtualFile) => {
    setDownloadingS3FileId(file.id);
    try {
      const result = await getS3DownloadUrl(file.s3_key, true);
      if (result.success && result.data?.url) {
        window.open(result.data.url, '_blank');
      } else {
        toast.error('Impossible de télécharger le fichier');
      }
    } catch (error) {
      console.error('Error downloading S3 file:', error);
      toast.error('Erreur lors du téléchargement');
    } finally {
      setDownloadingS3FileId(null);
    }
  };

  const handleDeleteS3File = async (file: VirtualFile) => {
    setDeletingS3FileId(file.id);
    try {
      const result = await deleteVirtualFile(file.id);
      if (result.success) {
        setS3Files(prev => prev.filter(f => f.id !== file.id));
        toast.success('Fichier supprimé');
      } else {
        toast.error(result.error || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting S3 file:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeletingS3FileId(null);
    }
  };
  const handleOpenFolder = (folder: UserAssetFolder) => {
    navigateToFolder(folder);
  };
  const handleNavigateBack = () => {
    // If we came from a deep link, go back to calendar with digest state preserved
    if (videoIdFromUrl) {
      navigate('/app/calendar', {
        state: {
          expandedDigestCategories: ['resources']
        }
      });
      return;
    }
    if (currentView === 'root') {
      navigate('/app/profile');
    } else if (currentView === 'archive' && folderPath.length === 0) {
      setCurrentView('root');
      resetNavigation();
    } else if (currentView === 'video' && folderPath.length === 0) {
      if (currentVideoContent && !EDITABLE_VIDEO_STATUSES.includes(currentVideoContent.status || '')) {
        setCurrentView('archive');
        resetNavigation();
      } else {
        setCurrentView('root');
        resetNavigation();
      }
    } else if (folderPath.length === 0) {
      setCurrentView('root');
      resetNavigation();
    } else {
      navigateBack();
    }
  };
  const handleOpenArchive = () => {
    setCurrentView('archive');
    setCurrentFolderId(null);
    setCurrentVideoContent(null);
    setFolderPath([]);
  };
  const handleDeleteAsset = async (assetId: string) => {
    if (!canModifyContent) return;
    try {
      await deleteUserAsset(assetId);
      removeAsset(assetId);
    } catch (error) {
      console.error('Error deleting asset:', error);
    }
  };
  const handleDeleteFolder = async (folderId: string) => {
    if (!canModifyContent) return;
    try {
      await deleteUserFolder(folderId);
      removeFolder(folderId);
    } catch (error) {
      console.error('Error deleting folder:', error);
    }
  };
  const handleDownload = async (asset: UserAsset) => {
    try {
      const signedUrl = await getAssetSignedUrl(asset.file_path);
      window.open(signedUrl, '_blank');
    } catch (error) {
      console.error('Error downloading asset:', error);
    }
  };
  const handleMoveAsset = async (targetFolderId: string | null) => {
    if (!assetToMove || !canModifyContent) return;
    setIsMoving(true);
    try {
      await updateUserAsset(assetToMove.id, {
        folder_id: targetFolderId
      });
      removeAsset(assetToMove.id);
      setShowMoveDialog(false);
      setAssetToMove(null);
    } catch (error) {
      console.error('Error moving asset:', error);
    } finally {
      setIsMoving(false);
    }
  };
  const handleRenameAsset = async () => {
    if (!assetToRename || !newAssetName.trim() || !canModifyContent) return;
    setIsRenaming(true);
    try {
      const updatedAsset = await updateUserAsset(assetToRename.id, {
        file_name: newAssetName.trim()
      });
      updateAsset(assetToRename.id, updatedAsset);
      setShowRenameDialog(false);
      setAssetToRename(null);
      setNewAssetName('');
    } catch (error) {
      console.error('Error renaming asset:', error);
    } finally {
      setIsRenaming(false);
    }
  };
  const handleRenameFolder = async () => {
    if (!folderToRename || !renameFolderName.trim() || !canModifyContent) return;
    setIsRenamingFolder(true);
    try {
      const updatedFolder = await updateUserFolder(folderToRename.id, {
        name: renameFolderName.trim()
      });
      updateFolder(folderToRename.id, updatedFolder);
      setShowRenameFolderDialog(false);
      setFolderToRename(null);
      setRenameFolderName('');
    } catch (error) {
      console.error('Error renaming folder:', error);
    } finally {
      setIsRenamingFolder(false);
    }
  };
  const handleToggleNoResources = async (checked: boolean) => {
    if (!currentVideoContent) return;
    setIsMarkingNoResources(true);
    try {
      await markVideoNoResourcesNeeded(currentVideoContent.id, checked);
      updateVideoContent(currentVideoContent.id, {
        ...currentVideoContent,
        resources_not_needed: checked
      });
    } catch (error) {
      console.error('Error toggling video resources status:', error);
    } finally {
      setIsMarkingNoResources(false);
    }
  };
  const handleToggleAllNoResources = async (checked: boolean) => {
    if (!userId || allDraftVideos.length === 0) return;
    setIsMarkingAllNoResources(true);
    try {
      await markAllVideosNoResourcesNeeded(userId, checked);
      const updatedVideos = videoContents.map(v => EDITABLE_VIDEO_STATUSES.includes(v.status || '') ? {
        ...v,
        resources_not_needed: checked
      } : v);
      updateVideoContents(updatedVideos);
    } catch (error) {
      console.error('Error toggling all videos resources status:', error);
    } finally {
      setIsMarkingAllNoResources(false);
    }
  };

  // Compute current folder name for header
  const getCurrentFolderName = () => {
    if (currentView === 'root') return 'Ressources';
    if (currentView === 'archive') {
      if (folderPath.length > 0) return folderPath[folderPath.length - 1].name;
      return 'Archive';
    }
    if (folderPath.length > 0) return folderPath[folderPath.length - 1].name;
    if (currentView === 'general') return 'Général';
    if (currentView === 'video' && currentVideoContent) {
      return currentVideoContent.title || `Vidéo ${currentVideoContent.id.slice(0, 8)}`;
    }
    return 'Ressources';
  };

  // Get header subtitle based on current view
  const getHeaderSubtitle = () => {
    if (currentView === 'root') {
      return `${videosWithPendingResources?.length || 0} vidéo(s) en attente de ressources`;
    }
    if (currentView === 'general') {
      return 'Fichiers partagés entre tous vos projets';
    }
    if (currentView === 'archive') {
      return `${archivedVideos.length} vidéo(s) archivée(s)`;
    }
    if (currentView === 'video' && currentVideoContent) {
      return isCurrentVideoEditable
        ? 'Ajoutez vos fichiers pour cette vidéo'
        : `${getVideoStatusLabel(currentVideoContent.status)} - Lecture seule`;
    }
    return '';
  };

  // Render loading spinner
  const renderLoadingSpinner = () => (
    <div className="flex items-center justify-center py-20">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
    </div>
  );

  // Render empty folder state
  const renderEmptyFolder = (message: string, canUpload: boolean) => (
    <div className="relative bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <Folder className="h-12 w-12 mb-3" />
        <p>{message}</p>
        {canUpload && canModifyContent && (
          <>
            <p className="text-sm text-muted-foreground/70 mt-1 mb-4">
              Ajoutez vos fichiers pour cette vidéo
            </p>
            <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer text-sm font-medium">
              <input
                type="file"
                multiple
                onChange={handleFileSelect}
                className="hidden"
                disabled={isUploading}
              />
              {isUploading ? (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground" />
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Parcourir les fichiers
                </>
              )}
            </label>
          </>
        )}
      </div>
    </div>
  );

  // Render archive empty state
  const renderArchiveEmpty = () => (
    <div className="relative bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
        <FileArchive className="h-12 w-12 mb-3" />
        <p>Aucune vidéo archivée</p>
      </div>
    </div>
  );

  const currentFolderName = getCurrentFolderName();
  const headerSubtitle = getHeaderSubtitle();
  return <div className="min-h-screen bg-black p-6 lg:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header - matching profile sub-pages pattern */}
        <div className="flex items-center gap-4 mb-8">
          <Button variant="ghost" size="icon" onClick={handleNavigateBack} className="text-white/60 hover:text-white hover:bg-white/10">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-semibold text-white">{currentFolderName}</h1>
            <p className="text-white/60 text-sm">{headerSubtitle}</p>
          </div>
          {currentView === 'video' && currentVideoContent && !isCurrentVideoEditable && <div className="flex items-center gap-2 text-sm text-white/40 bg-white/5 px-3 py-1.5 rounded-lg">
              <Lock className="h-4 w-4" />
            </div>}
        </div>

        {/* Toolbar */}
        

        <div className="space-y-4">
        {/* Root View - Show General folder and Video folders */}
        {currentView === 'root' ? <>
            {isLoading ? <div className="flex items-center justify-center py-20">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div> : <div className="relative bg-card border border-border rounded-xl overflow-hidden">
                <div className="divide-y divide-border">
                  {/* General Folder */}
                  <button
                    type="button"
                    className="w-full flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors text-left"
                    onClick={handleOpenGeneralFolder}
                  >
                    <div className="w-10 shrink-0 flex justify-center">
                      <Folder className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-foreground">Général</p>
                      <p className="text-xs text-muted-foreground">
                        Vos fichiers personnels
                      </p>
                    </div>
                    <ChevronRight className="h-5 w-5 text-muted-foreground" />
                  </button>

                  {/* Video Folders - Show all active videos */}
                  {activeVideos.map(video => {
                const isPendingResources = videosWithPendingResources.some(pv => pv.id === video.id);
                const videoHasAssets = !isPendingResources;
                const showGreenCheck = hasGreenCheck(video, videoHasAssets);
                const isEditable = isVideoEditable(video);
                const clapperboardColorClass = getClapperboardColorClass(isPendingResources, isEditable);
                const titleClass = isEditable ? getEditableVideoTitleClass() : getReadOnlyVideoTitleClass();
                const handleClick = () => handleOpenVideoFolder(video);
                return (
                      <button
                        key={video.id}
                        type="button"
                        className="w-full flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors text-left"
                        onClick={handleClick}
                      >
                        <div className="w-10 shrink-0 flex justify-center">
                          <Clapperboard className={`h-5 w-5 ${clapperboardColorClass}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`font-medium truncate ${titleClass}`}>
                            {video.title || `Vidéo ${video.id.slice(0, 8)}`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {getVideoStatusLabel(video.status)}
                          </p>
                        </div>
                        {showGreenCheck && <CheckCircle2 className="h-5 w-5 text-success" />}
                        {!isEditable && <Lock className="h-4 w-4 text-muted-foreground" />}
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </button>
                    );
              })}

                  {/* Archive Folder */}
                  {archivedVideos.length > 0 && (
                    <button
                      type="button"
                      className="w-full flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors text-left"
                      onClick={handleOpenArchive}
                    >
                      <div className="w-10 shrink-0 flex justify-center">
                        <FileArchive className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-muted-foreground">Archive</p>
                        <p className="text-xs text-muted-foreground">
                          {archivedVideos.length} vidéo{archivedVideos.length > 1 ? 's' : ''} publiée{archivedVideos.length > 1 ? 's' : ''}
                        </p>
                      </div>
                      <Lock className="h-4 w-4 text-muted-foreground" />
                      <ChevronRight className="h-5 w-5 text-muted-foreground" />
                    </button>
                  )}
                </div>

                {/* Global no resources toggle - sticky at bottom */}
                {allDraftVideos.length > 0 && (
                  <div
                    className="flex items-center justify-between px-4 py-3 border-t border-border bg-muted/20"
                  >
                    <span className="text-sm text-muted-foreground">Je n'ai pas de ressources personnelles pour mes vidéos</span>
                    <Switch checked={allDraftVideosNoResources} disabled={isMarkingAllNoResources} onCheckedChange={handleToggleAllNoResources} />
                  </div>
                )}

                {videoContents.length === 0 && activeVideos.length === 0 && <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                    <Clapperboard className="h-8 w-8 mb-2 opacity-40" />
                    <p className="text-sm">Aucune vidéo</p>
                  </div>}
              </div>}
          </> : currentView === 'archive' ? <>
            {/* Archive View - Show archived videos */}
            {isLoading && renderLoadingSpinner()}
            {!isLoading && archivedVideos.length === 0 && renderArchiveEmpty()}
            {!isLoading && archivedVideos.length > 0 && (
              <div className="relative bg-card border border-border rounded-xl overflow-hidden">
                <div className="divide-y divide-border">
                  {archivedVideos.map(video => {
                    const handleClick = () => handleOpenVideoFolder(video);
                    return (
                      <button
                        key={video.id}
                        type="button"
                        className="w-full flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-muted/50 transition-colors text-left"
                        onClick={handleClick}
                      >
                        <div className="w-10 shrink-0 flex justify-center">
                          <Clapperboard className="h-5 w-5 text-muted-foreground" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate text-muted-foreground">
                            {video.title || `Vidéo ${video.id.slice(0, 8)}`}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {getVideoStatusLabel(video.status)}
                          </p>
                        </div>
                        <Lock className="h-4 w-4 text-muted-foreground" />
                        <ChevronRight className="h-5 w-5 text-muted-foreground" />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </> : <>
            {/* No resources toggle - only for video folders at root level */}
            {currentView === 'video' && currentVideoContent && folderPath.length === 0 && canModifyContent && <div className="flex items-center justify-between p-3 rounded-lg bg-muted/30 border border-border mb-4">
                <span className="text-sm text-muted-foreground">Pas de ressources pour cette vidéo</span>
                <Switch checked={currentVideoContent.resources_not_needed === true} disabled={isMarkingNoResources} onCheckedChange={handleToggleNoResources} />
              </div>}

            {/* Content */}
            {(isLoading || isLoadingS3Files) && renderLoadingSpinner()}

            {/* S3 Storage view - Empty state */}
            {!(isLoading || isLoadingS3Files) && useS3Storage && s3Files.length === 0 &&
              renderEmptyFolder('Ce dossier est vide', true)
            }

            {/* S3 Storage view - With files */}
            {!(isLoading || isLoadingS3Files) && useS3Storage && s3Files.length > 0 && (
                <div className="relative bg-card border border-border rounded-xl overflow-hidden">
                  {/* Upload button for S3 */}
                  {canModifyContent && (
                    <div className="p-4 border-b border-border">
                      <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer text-sm font-medium">
                        <input
                          type="file"
                          multiple
                          onChange={handleFileSelect}
                          className="hidden"
                          disabled={isUploading}
                        />
                        {isUploading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground" />
                        ) : (
                          <>
                            <Upload className="h-4 w-4" />
                            Ajouter des fichiers
                          </>
                        )}
                      </label>
                    </div>
                  )}
                  {/* S3 Files list */}
                  <div className="divide-y divide-border">
                    {s3Files.map(file => {
                      const FileIcon = getFileIcon(file.display_name.split('.').pop() || null);
                      const isDeleting = deletingS3FileId === file.id;
                      const isDownloading = downloadingS3FileId === file.id;
                      
                      return (
                        <div key={file.id} className="flex items-center gap-4 px-4 py-3 transition-all hover:bg-muted/50">
                          {/* Icon */}
                          <div className="w-10 shrink-0 flex justify-center">
                            <div className="text-primary">
                              <FileIcon className="h-5 w-5" />
                            </div>
                          </div>

                          {/* Name */}
                          <div className="flex-1 min-w-0">
                            <p className="truncate text-foreground">
                              {file.display_name}
                            </p>
                          </div>

                          {/* Date */}
                          <div className="w-36 shrink-0 text-sm text-muted-foreground">
                            {file.last_modified ? new Date(file.last_modified).toLocaleDateString('fr-FR') : '-'}
                          </div>

                          {/* Size */}
                          <div className="w-24 shrink-0 text-sm text-muted-foreground text-right">
                            {formatFileSize(file.file_size)}
                          </div>

                          {/* Actions */}
                          <div className="w-20 shrink-0 flex justify-end gap-1">
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={() => handleDownloadS3File(file)} 
                              disabled={isDownloading}
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                            >
                              {isDownloading ? (
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </Button>
                            {canModifyContent && (
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                onClick={() => handleDeleteS3File(file)}
                                disabled={isDeleting}
                                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                              >
                                {isDeleting ? (
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
            )}

            {/* Old system - Empty state */}
            {!(isLoading || isLoadingS3Files) && !useS3Storage && folders.length === 0 && assets.length === 0 && (
              <div className="relative bg-card border border-border rounded-xl overflow-hidden">
                <div className="flex flex-col items-center justify-center py-20 text-muted-foreground">
                  <Folder className="h-12 w-12 mb-3" />
                  <p>Ce dossier est vide</p>
                  {canModifyContent && (
                    <>
                      <p className="text-sm text-muted-foreground/70 mt-1 mb-4">
                        Ajoutez des fichiers ou créez des dossiers
                      </p>
                      <label className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors cursor-pointer text-sm font-medium">
                        <input
                          type="file"
                          multiple
                          onChange={handleFileSelect}
                          className="hidden"
                          disabled={isUploading}
                        />
                        {isUploading ? (
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-foreground" />
                        ) : (
                          <>
                            <Upload className="h-4 w-4" />
                            Parcourir les fichiers
                          </>
                        )}
                      </label>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* Old system - With content */}
            {!(isLoading || isLoadingS3Files) && !useS3Storage && (folders.length > 0 || assets.length > 0) && (
              <div className="relative bg-card border border-border rounded-xl overflow-hidden">
                {/* Items */}
                <div className="divide-y divide-border">
                  {/* Folders */}
                  {folders.map(folder => {
                    const handleClick = () => handleOpenFolder(folder);
                    return (
                      <div
                        key={folder.id}
                        className="flex items-center gap-4 px-4 py-3 transition-all hover:bg-muted/50"
                      >
                        {/* Clickable area for folder navigation */}
                        <button
                          type="button"
                          className="flex items-center gap-4 flex-1 min-w-0 cursor-pointer text-left"
                          onClick={handleClick}
                        >
                          {/* Icon */}
                          <div className="w-10 shrink-0 flex justify-center">
                            <div className="text-warning">
                              <Folder className="h-5 w-5" />
                            </div>
                          </div>

                          {/* Name */}
                          <div className="flex-1 min-w-0">
                            <p className="truncate font-medium text-foreground">
                              {folder.name}
                            </p>
                          </div>

                          {/* Date */}
                          <div className="w-36 shrink-0 text-sm text-muted-foreground">
                            {new Date(folder.created_at).toLocaleDateString('fr-FR')}
                          </div>
                        </button>

                        {/* Actions */}
                        <div className="w-20 shrink-0 flex justify-end">
                          {canModifyContent && <DropdownMenu>
                              <DropdownMenuTrigger onClick={e => e.stopPropagation()} className="h-8 w-8 flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground">
                                <MoreVertical className="h-4 w-4" />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={e => {
                          e.stopPropagation();
                          setFolderToRename(folder);
                          setRenameFolderName(folder.name);
                          setShowRenameFolderDialog(true);
                        }}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Renommer
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={e => {
                          e.stopPropagation();
                          handleDeleteFolder(folder.id);
                        }} className="text-destructive">
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Supprimer
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>}
                        </div>
                      </div>
                    );
                  })}

                  {/* Assets */}
                  {assets.map(asset => {
                const FileIcon = getFileIcon(asset.file_type);
                return <div key={asset.id} className="flex items-center gap-4 px-4 py-3 transition-all hover:bg-muted/50">
                        {/* Icon */}
                        <div className="w-10 shrink-0 flex justify-center">
                          <div className="text-primary">
                            <FileIcon className="h-5 w-5" />
                          </div>
                        </div>

                        {/* Name */}
                        <div className="flex-1 min-w-0">
                          <p className="truncate text-foreground">
                            {asset.file_name}
                          </p>
                        </div>

                        {/* Date */}
                        <div className="w-36 shrink-0 text-sm text-muted-foreground">
                          {new Date(asset.created_at).toLocaleDateString('fr-FR')}
                        </div>

                        {/* Size */}
                        <div className="w-24 shrink-0 text-sm text-muted-foreground text-right">
                          {formatFileSize(asset.file_size)}
                        </div>

                        {/* Actions */}
                        <div className="w-20 shrink-0 flex justify-end gap-1">
                          <Button variant="ghost" size="icon" onClick={() => handleDownload(asset)} className="h-8 w-8 text-muted-foreground hover:text-foreground">
                            <Download className="h-4 w-4" />
                          </Button>
                          {canModifyContent && <DropdownMenu>
                              <DropdownMenuTrigger className="h-8 w-8 flex items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground">
                                <MoreVertical className="h-4 w-4" />
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => {
                          setAssetToRename(asset);
                          setNewAssetName(asset.file_name);
                          setShowRenameDialog(true);
                        }}>
                                  <Pencil className="h-4 w-4 mr-2" />
                                  Renommer
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {
                          setAssetToMove(asset);
                          setShowMoveDialog(true);
                        }}>
                                  <FolderInput className="h-4 w-4 mr-2" />
                                  Déplacer
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteAsset(asset.id)} className="text-destructive">
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Supprimer
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>}
                        </div>
                      </div>;
              })}
                </div>
              </div>
            )}
          </>}
        </div>
      </div>

      {/* New Folder Dialog */}
      <Dialog open={showNewFolderDialog} onOpenChange={setShowNewFolderDialog}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Nouveau dossier</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Entrez le nom du nouveau dossier
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input value={newFolderName} onChange={e => setNewFolderName(e.target.value)} placeholder="Nom du dossier" className="bg-background border-border" onKeyDown={e => {
            if (e.key === 'Enter' && newFolderName.trim()) {
              handleCreateFolder();
            }
          }} />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => {
              setShowNewFolderDialog(false);
              setNewFolderName('');
            }}>
                Annuler
              </Button>
              <Button onClick={handleCreateFolder} disabled={!newFolderName.trim() || isCreatingFolder} className="bg-primary hover:bg-primary/90">
                {isCreatingFolder ? 'Création...' : 'Créer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Move Asset Dialog */}
      <Dialog open={showMoveDialog} onOpenChange={open => {
      setShowMoveDialog(open);
      if (!open) setAssetToMove(null);
    }}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Déplacer "{assetToMove?.file_name}"</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Sélectionnez le dossier de destination
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 pt-4 max-h-[300px] overflow-y-auto">
            {/* Root option */}
            <button onClick={() => handleMoveAsset(null)} disabled={isMoving || assetToMove?.folder_id === null} className="w-full flex items-center gap-3 p-3 rounded-lg bg-background hover:bg-accent transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
              <Folder className="h-5 w-5 text-warning" />
              <span className="text-foreground">Mes assets (racine)</span>
            </button>
            
            {/* Folders */}
            {allFolders.filter(f => f.id !== assetToMove?.folder_id).map(folder => <button key={folder.id} onClick={() => handleMoveAsset(folder.id)} disabled={isMoving} className="w-full flex items-center gap-3 p-3 rounded-lg bg-background hover:bg-accent transition-colors disabled:opacity-50">
                  <Folder className="h-5 w-5 text-warning" />
                  <span className="text-foreground">{folder.name}</span>
                </button>)}
            
            {allFolders.length === 0 && <p className="text-center text-muted-foreground py-4">
                Aucun dossier disponible
              </p>}
          </div>
          <div className="flex justify-end pt-4">
            <Button variant="outline" onClick={() => {
            setShowMoveDialog(false);
            setAssetToMove(null);
          }}>
              Annuler
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename Asset Dialog */}
      <Dialog open={showRenameDialog} onOpenChange={open => {
      setShowRenameDialog(open);
      if (!open) {
        setAssetToRename(null);
        setNewAssetName('');
      }
    }}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Renommer le fichier</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Entrez le nouveau nom du fichier
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input value={newAssetName} onChange={e => setNewAssetName(e.target.value)} placeholder="Nom du fichier" className="bg-background border-border" onKeyDown={e => {
            if (e.key === 'Enter' && newAssetName.trim()) {
              handleRenameAsset();
            }
          }} />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => {
              setShowRenameDialog(false);
              setAssetToRename(null);
              setNewAssetName('');
            }}>
                Annuler
              </Button>
              <Button onClick={handleRenameAsset} disabled={!newAssetName.trim() || isRenaming} className="bg-primary hover:bg-primary/90">
                {isRenaming ? 'Renommage...' : 'Renommer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rename Folder Dialog */}
      <Dialog open={showRenameFolderDialog} onOpenChange={open => {
      setShowRenameFolderDialog(open);
      if (!open) {
        setFolderToRename(null);
        setRenameFolderName('');
      }
    }}>
        <DialogContent className="bg-card border-border">
          <DialogHeader>
            <DialogTitle className="text-foreground">Renommer le dossier</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Entrez le nouveau nom du dossier
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <Input value={renameFolderName} onChange={e => setRenameFolderName(e.target.value)} placeholder="Nom du dossier" className="bg-background border-border" onKeyDown={e => {
            if (e.key === 'Enter' && renameFolderName.trim()) {
              handleRenameFolder();
            }
          }} />
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => {
              setShowRenameFolderDialog(false);
              setFolderToRename(null);
              setRenameFolderName('');
            }}>
                Annuler
              </Button>
              <Button onClick={handleRenameFolder} disabled={!renameFolderName.trim() || isRenamingFolder} className="bg-primary hover:bg-primary/90">
                {isRenamingFolder ? 'Renommage...' : 'Renommer'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>;
}